import React, { useEffect, useRef } from 'react';
import { Animated, StyleProp, ViewStyle } from 'react-native';
import LoadingContent from './LoadingContent';
import { usePrevious } from '../../../hooks/usePrevious';

export type AnimateLoadedContentOwnProps = {
    children: () => React.ReactNode;
    isLoading: boolean;
    style?: StyleProp<ViewStyle>;
    message?: string;
};

const ANIMATION_DURATION = 300;

export type AnimateLoadedContentProps = AnimateLoadedContentOwnProps;

const containerStyles: StyleProp<ViewStyle> = {
    width: '100%',
    height: '100%',
};

const AnimateLoadedContent: React.FC<AnimateLoadedContentProps> = (props) => {
    // useRerenderOnInterval({ interval: 1000 });
    const animateOutRef = useRef<Animated.Value>(new Animated.Value(props.isLoading ? 1 : 0));
    const animateInRef = useRef<Animated.Value>(new Animated.Value(props.isLoading ? 0 : 1));
    const [isMainContentIn, setIsMainContentIn] = React.useState(!props.isLoading);
    const previousIsLoading = usePrevious(props.isLoading, () => {
        if (props.isLoading) {
            setIsMainContentIn(false);
        }
    });
    const [isAnimating, setIsAnimating] = React.useState(false);
    // Identifies the current pass through the effect. The cleanup below calls stopAnimation(), which
    // fires the in-flight callbacks with finished: false — so the flag has to be cleared on
    // interruption too, or `animating-parent` is stranded on this subtree for good. The id stops a
    // superseded pass from clearing the flag that its replacement has already re-armed.
    const animationRunIdRef = useRef(0);
    useEffect(() => {
        const runId = ++animationRunIdRef.current;
        const ownsAnimation = () => animationRunIdRef.current === runId;
        if (props.isLoading) {
            animateOutRef.current.setValue(1);
            animateInRef.current.setValue(0);
            setIsMainContentIn(false);
            setIsAnimating(true);
        } else if (previousIsLoading) {
            animateOutRef.current.setValue(1);
            animateInRef.current.setValue(0);
            setIsMainContentIn(false);
            setIsAnimating(true);
            const subAnimationDuration = ANIMATION_DURATION / 2;
            Animated.timing(animateOutRef.current, {
                toValue: 0,
                duration: subAnimationDuration,
                useNativeDriver: true, // Platform.OS === 'web',
            }).start((args) => {
                if (!ownsAnimation()) return;
                if (!args.finished) {
                    setIsAnimating(false);
                    return;
                }
                setIsMainContentIn(true);
                Animated.timing(animateInRef.current, {
                    toValue: 1,
                    duration: subAnimationDuration,
                    useNativeDriver: true, // Platform.OS === 'web',
                }).start(({ finished }) => {
                    if (!ownsAnimation()) return;
                    if (finished) {
                        setIsMainContentIn(true);
                    }
                    setIsAnimating(false);
                });
            });

            return () => {
                animateOutRef.current.stopAnimation();
                animateInRef.current.stopAnimation();
                // eslint-disable-next-line react-hooks/exhaustive-deps
                animateOutRef.current.setValue(0);
                // eslint-disable-next-line react-hooks/exhaustive-deps
                animateInRef.current.setValue(1);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isLoading]);

    return isMainContentIn ? (
        <Animated.View
            style={[
                containerStyles,
                {
                    opacity: animateInRef.current,
                },
                props.style,
            ]}
            testID={isAnimating ? 'animating-parent' : undefined}
        >
            {props.children()}
        </Animated.View>
    ) : (
        <Animated.View
            style={[
                containerStyles,
                {
                    opacity: animateOutRef.current,
                },
                props.style,
            ]}
        >
            <LoadingContent message={props.message} />
        </Animated.View>
    );
};

export default React.memo(AnimateLoadedContent);
